<template>
  <v-tooltip class="ui-tooltip-icon" location="bottom">
    <template #activator="{ props: activatorProps }">
      <span
        v-bind="activatorProps"
        class="ui-tooltip-icon__activator"
        :class="$attrs.class"
        :style="$attrs.style"
        @click.stop
      >
        <ui-icon
          class="ui-tooltip-icon__icon cursor-pointer"
          :size="String(size)"
          :color="pColor"
          :icon="pIcon"
        />
      </span>
    </template>
    <slot />
    <span v-if="text" class="ui-tooltip-icon__text" v-html="text" />
  </v-tooltip>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useColors } from '@/composables';

export interface UiTooltipIconPreset {
  name: string;
  icon: string;
  color: string;
}

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    icon?: string;
    color?: string;
    text?: string;
    size?: number | string;
    preset?: string;
  }>(),
  {
    size: 12,
  },
);

const $colors = useColors();

const presets = computed((): UiTooltipIconPreset[] => [
  {
    name: 'help',
    icon: 'fas fa-question-circle',
    color: $colors.green,
  },
  {
    name: 'warning',
    icon: 'fas fa-exclamation-triangle',
    color: $colors.sunglow,
  },
  {
    name: 'error',
    icon: 'fas fa-exclamation-circle',
    color: $colors.red,
  },
]);

const activePreset = computed(() =>
  presets.value.find((item) => item.name === props.preset),
);

const pIcon = computed(
  () => props.icon || activePreset.value?.icon || 'fas fa-question-circle',
);

const pColor = computed(() => props.color || activePreset.value?.color);
</script>

<style lang="scss">
.ui-tooltip-icon {
  .ui-tooltip-icon__activator {
    align-items: center;
    display: inline-flex;
  }

  & &__icon {
    font-size: 13px;
    line-height: 1.23;

    &--color-accent {
      color: var(--color-green);
    }

    &--color-error {
      color: var(--color-red);
    }

    &--color-warning {
      color: var(--color-sunglow);
    }
  }
}
</style>
